import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import { AlertCircle, Network, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import Paginator from '@/components/Paginator'
import { FreshnessBanner } from './FreshnessBanner'

/**
 * `/settings/knowledge-graph` — internal eng-ops surface (AGNT-698).
 *
 * Lists every upload in the tenant with its upload→KG pipeline state:
 * processing status, retry count, error message, age. The audit on dev
 * surfaced a 964-row pnid backlog that this page makes visible without
 * a kubectl session.
 *
 * Phases shipped:
 *  - 1: list + filters + needs-attention default view.
 *  - 2B: per-row Reingest button → fires regen against postgres-stored
 *    aggregated_data.json (no raw-file re-upload).
 */

type ProcessingStatus = 'pending' | 'queued' | 'processing' | 'completed' | 'failed' | 'skipped'
type FileType =
  | 'document'
  | 'pnid'
  | 'pfd'
  | 'excel'
  | 'datasheet'
  | 'timeseries'
  | 'specsheet'
  | 'json'
  | 'audio'
  | 'image'
  | 'other'

interface KgUploadListItem {
  id: string
  original_filename: string
  file_size: number | null
  file_type: FileType
  processing_status: ProcessingStatus
  processing_started_at: string | null
  processing_completed_at: string | null
  processing_error_message: string | null
  processing_retry_count: number
  created_at: string
  kg_files_emitted: string[] | null
  /**
   * VISN-150 axis 1 — per-row "Reprocess available" badge signal.
   * `true` when the row's `parsed_features` is missing one or more
   * tags from `EXPECTED_PARSED_FEATURES[file_type]` on the BE.
   * `missing_features` is the ordered list of expected tags absent
   * from the row's stored set (used as the tooltip body).
   *
   * `reprocess_recommended` defaults to `false` and
   * `missing_features` defaults to `[]` so renders against a
   * pre-VISN-150 list response (e.g. a stale test fixture) don't
   * crash on undefined access.
   */
  reprocess_recommended?: boolean
  missing_features?: string[]
}

interface ListResponse {
  uploads: KgUploadListItem[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

/**
 * Matches the backend constant `STUCK_THRESHOLD_HOURS` in
 * `backend/web/src/knowledge-graph/dto/list-uploads.dto.ts`. The row
 * red-dot uses this client-side to highlight individual rows that
 * the server would also classify as stuck — they may be visible
 * even outside `view=needs_attention` (e.g. when the user has switched
 * to "All statuses").
 */
const STUCK_THRESHOLD_MS = 24 * 60 * 60 * 1000


function isStuck(u: KgUploadListItem): boolean {
  if (u.processing_status !== 'queued' && u.processing_status !== 'processing') return false
  if (!u.processing_started_at) return false
  return Date.now() - new Date(u.processing_started_at).getTime() > STUCK_THRESHOLD_MS
}

const STATUS_CONFIG: Record<
  ProcessingStatus,
  { textClass: string; bgClass: string; dotClass: string }
> = {
  completed: {
    textClass: 'text-green-700 dark:text-green-400',
    bgClass: 'bg-green-100 dark:bg-green-900/30',
    dotClass: 'bg-green-500',
  },
  processing: {
    textClass: 'text-blue-700 dark:text-blue-400',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    dotClass: 'bg-blue-500',
  },
  queued: {
    textClass: 'text-amber-700 dark:text-amber-400',
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    dotClass: 'bg-amber-500',
  },
  failed: {
    textClass: 'text-red-700 dark:text-red-400',
    bgClass: 'bg-red-100 dark:bg-red-900/30',
    dotClass: 'bg-red-500',
  },
  pending: {
    textClass: 'text-gray-700 dark:text-gray-300',
    bgClass: 'bg-gray-100 dark:bg-dark-accent',
    dotClass: 'bg-gray-500',
  },
  skipped: {
    textClass: 'text-gray-500 dark:text-gray-400',
    bgClass: 'bg-gray-100 dark:bg-dark-accent',
    dotClass: 'bg-gray-400',
  },
}

const FILE_TYPES: FileType[] = [
  'document',
  'pnid',
  'pfd',
  'excel',
  'datasheet',
  'timeseries',
  'specsheet',
  'json',
  'audio',
  'image',
  'other',
]

const PROCESSING_STATUSES: ProcessingStatus[] = [
  'pending',
  'queued',
  'processing',
  'completed',
  'failed',
  'skipped',
]

function formatAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  return `${d}d`
}

function formatBytes(b: number | null): string {
  if (b === null) return '—'
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export default function UploadsTab() {
  const { t } = useTranslation()
  const [uploads, setUploads] = useState<KgUploadListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState<ProcessingStatus | ''>('')
  const [typeFilter, setTypeFilter] = useState<FileType | ''>('')
  const [search, setSearch] = useState('')
  // The page lands on `needs_attention` so engineers see what's broken
  // first instead of a wall of "completed" green. Toggling the chip
  // clears it; the explicit status/type filters still apply on top.
  const [needsAttentionOnly, setNeedsAttentionOnly] = useState(true)
  // Per-row in-flight set so the user can't click "Reingest" twice
  // on the same row while the first request is still landing. Two
  // jobs would land on the BullMQ queue (so it's safe-but-redundant,
  // not idempotent at the queue level — copilot #11863 follow-up)
  // and the worker would do the same regen twice; the UI gate keeps
  // the queue tidy and the user signal honest.
  const [reingesting, setReingesting] = useState<Set<string>>(new Set())
  // VISN-150 axis 1 remediation — separate in-flight set for the
  // re-PARSE action (distinct from re-INGEST). They're separate state
  // because a row can legitimately have both in flight (a reparse
  // triggers a downstream kg-regen, but a manual reingest of a
  // different row at the same time should not gate this row's
  // reparse button on it).
  const [reparsing, setReparsing] = useState<Set<string>>(new Set())
  // Bump to force the list re-fetch after a successful reingest so
  // the user sees the row flip to `queued`/`processing` without
  // having to manually reload.
  const [refreshNonce, setRefreshNonce] = useState(0)
  // Debounce `search` separately from the rest of the filter state so
  // typing "pnid_diagram" doesn't fire 12 concurrent requests
  // (greptile P1 #11684). 300ms is long enough to coalesce a quick
  // burst, short enough that the user sees results before they have
  // time to wonder whether they typed it correctly.
  const [debouncedSearch, setDebouncedSearch] = useState(search)
  useEffect(() => {
    if (search === debouncedSearch) return
    const handle = window.setTimeout(() => setDebouncedSearch(search), 300)
    return () => window.clearTimeout(handle)
  }, [search, debouncedSearch])

  useEffect(() => {
    let cancelled = false
    const fetch = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          pageSize: pageSize.toString(),
        })
        if (needsAttentionOnly) params.append('view', 'needs_attention')
        if (statusFilter) params.append('processingStatus', statusFilter)
        if (typeFilter) params.append('fileType', typeFilter)
        if (debouncedSearch) params.append('search', debouncedSearch)
        const res = await apiService.fetchOrError(
          apiService.getUrl(`v1/knowledge-graph/uploads?${params.toString()}`),
        )
        const data: ListResponse = await res.json()
        if (!cancelled) {
          setUploads(data.uploads)
          setTotal(data.total)
        }
      } catch (err) {
        console.error('[KnowledgeGraph] fetch failed:', err)
        if (!cancelled) {
          setUploads([])
          // Clear `total` too so the Paginator doesn't render
          // "1–25 of 964" alongside the empty-state row when an
          // unrelated backend hiccup nukes the response.
          // (greptile P1 #11684.)
          setTotal(0)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetch()
    return () => {
      cancelled = true
    }
  }, [page, pageSize, statusFilter, typeFilter, debouncedSearch, needsAttentionOnly, refreshNonce])

  const reingest = useCallback(
    async (uploadId: string) => {
      // Mark the row as in-flight first so a quick double-click
      // doesn't fire two requests. We use a Set rather than a
      // single string because a future "reingest all" bulk action
      // (not in this PR) would want the same state shape.
      setReingesting((prev) => {
        const next = new Set(prev)
        next.add(uploadId)
        return next
      })
      try {
        const res = await apiService.fetchOrError(
          apiService.getUrl(`v1/knowledge-graph/uploads/${encodeURIComponent(uploadId)}/reingest`),
          { method: 'POST' },
        )
        const data: { job_id?: string } = await res.json()
        toast.success(
          t('common:knowledge_graph.reingest_enqueued', {
            jobId: data.job_id ?? '?',
            defaultValue: `Reingest enqueued (job ${data.job_id ?? '?'})`,
          }),
        )
        // Re-fetch the list so the row reflects its new
        // `processing_status` (queued → processing → completed).
        // Bumping a nonce is cheaper than threading a refetch
        // callback through the useEffect deps. Note: the immediate
        // list-refresh fires before the worker commits to Gitea —
        // the row status will lag until the user manually
        // refreshes or filters. We accepted that trade-off rather
        // than carrying optimistic state + a reconcile timer; this
        // page is a diagnostics surface, not a real-time dashboard.
        setRefreshNonce((n) => n + 1)
      } catch (err) {
        console.error('[KnowledgeGraph] reingest failed:', err)
        toast.error(
          t(
            'common:knowledge_graph.reingest_failed',
            'Reingest failed — see browser console for details.',
          ),
        )
      } finally {
        setReingesting((prev) => {
          const next = new Set(prev)
          next.delete(uploadId)
          return next
        })
      }
    },
    [t],
  )

  /**
   * VISN-150 axis 1 remediation — re-run the raw-file parser worker.
   *
   * Distinct from `reingest` above: that one re-fires kg-regen
   * against the already-extracted `aggregated_data.json`; this one
   * re-PARSES the source PDF/XLSX so `parsed_features` picks up the
   * tags the live parser ships. The kg-regen step happens naturally
   * on the parser worker's success path — no separate regen call
   * needed here.
   *
   * Backs the "Reprocess" button shown only when the row carries
   * the "Reprocess available" badge.
   */
  const reparse = useCallback(
    async (uploadId: string) => {
      setReparsing((prev) => {
        const next = new Set(prev)
        next.add(uploadId)
        return next
      })
      try {
        const res = await apiService.fetchOrError(
          apiService.getUrl(`v1/knowledge-graph/uploads/${encodeURIComponent(uploadId)}/reparse`),
          { method: 'POST' },
        )
        const data: { job_id?: string } = await res.json()
        toast.success(
          t('common:knowledge_graph.reparse_enqueued', {
            jobId: data.job_id ?? '?',
            defaultValue: `Reparse enqueued (job ${data.job_id ?? '?'})`,
          }),
        )
        // Bump the list-refresh so the row flips to `queued` /
        // `processing` immediately — the badge stays until the
        // parser commits new `parsed_features`.
        setRefreshNonce((n) => n + 1)
      } catch (err) {
        console.error('[KnowledgeGraph] reparse failed:', err)
        toast.error(
          t(
            'common:knowledge_graph.reparse_failed',
            'Reparse failed — see browser console for details.',
          ),
        )
      } finally {
        setReparsing((prev) => {
          const next = new Set(prev)
          next.delete(uploadId)
          return next
        })
      }
    },
    [t],
  )

  // VISN-150 axis 1 — tenant-wide reparse fan-out (bulk action).
  // Distinct from `reparsing` (per-row in-flight set) because the
  // bulk button gates on its own loading state — a single click
  // fans out N requests on the BE, so the UI gate just needs a
  // page-wide "in flight" boolean.
  const [reparseAllInFlight, setReparseAllInFlight] = useState(false)

  /**
   * Trigger the tenant-wide reparse-stale fan-out after a
   * window.confirm gate. Reparse is significantly heavier than the
   * sibling axis-2 regen-stale (minutes of vision compute per
   * PNID, vs. seconds of generator work), so we lean on the
   * browser's confirm dialog for the "are you sure" UX — internal
   * eng surface, no need for a custom modal component.
   *
   * The BE owns the per-row diff (`reprocess_recommended` against
   * the live catalog) and per-row failure isolation, so the FE
   * just posts once and toasts the returned count.
   */
  const reparseAllStale = useCallback(async () => {
    // Hard-coded English copy in the confirm because window.confirm
    // doesn't render translated strings reliably across browsers —
    // i18n the toast that follows, not the confirm.
    const ok = window.confirm(
      'Reprocess every upload with parser-feature drift?\n\n' +
        'This re-runs the raw-file parser (vision pipeline) for each ' +
        'matching row — typically several minutes per PNID and dollars ' +
        'of vision-API spend. Reingest fan-out is the cheaper option ' +
        'if you only need to refresh the knowledge graph from existing ' +
        'aggregated_data.json files.',
    )
    if (!ok) return
    setReparseAllInFlight(true)
    try {
      const res = await apiService.fetchOrError(
        apiService.getUrl('v1/knowledge-graph/uploads/reparse-stale'),
        { method: 'POST' },
      )
      const data: { enqueued_count: number } = await res.json()
      toast.success(
        t('common:knowledge_graph.reparse_stale_enqueued', {
          count: data.enqueued_count,
          defaultValue: `Queued ${data.enqueued_count} reparses — rows will flip to processing as the parser worker picks them up.`,
        }),
      )
      // Force a list refetch so the user sees the affected rows
      // flip to `queued` straight away. The actual parser
      // completion arrives much later (per-row, async).
      setRefreshNonce((n) => n + 1)
    } catch (err) {
      console.error('[KnowledgeGraph] reparse-stale failed:', err)
      toast.error(
        t(
          'common:knowledge_graph.reparse_stale_failed',
          'Bulk reparse failed — see browser console for details.',
        ),
      )
    } finally {
      setReparseAllInFlight(false)
    }
    // `apiService` is a module singleton and won't change identity
    // at runtime, but listing it here keeps the callback honest if
    // a future test/dev refactor swaps the import for an injected
    // context, and satisfies the react-hooks exhaustive-deps lint
    // if/when it gets enabled (greptile P2 #12229). `setStateFn`
    // refs are stable so they're omitted by React-team convention.
  }, [t, apiService])

  // Counters for the "needs attention" affordance (red + amber together).
  // Computed locally over the visible page only — for an authoritative
  // tenant-wide count we'd add a dedicated `/counts` endpoint in phase 3.
  const visibleFailed = useMemo(
    () => uploads.filter((u) => u.processing_status === 'failed').length,
    [uploads],
  )

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
            <Network className="h-5 w-5 text-purple-700 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">
              {t('common:knowledge_graph.title', 'Knowledge Graph')}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-muted">
              {t(
                'common:knowledge_graph.description',
                'Every uploaded file and its processing state. Failed or stuck uploads surface here before they reach the agent.',
              )}
            </p>
          </div>
        </div>
        {visibleFailed > 0 && (
          <div
            className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
            data-testid="kg-failed-banner"
          >
            <AlertCircle className="h-4 w-4" />
            {t('common:knowledge_graph.failed_on_page', {
              count: visibleFailed,
              defaultValue: `${visibleFailed} failed on this page`,
            })}
          </div>
        )}
      </div>

      {/* VISN-150 axis 2 — tenant-wide freshness banner. The banner
          renders only when the tenant is meaningfully behind (in_sync
          === false) or when the backend can't determine freshness
          (in_sync === null, with an error_code). The refreshNonce
          drives a re-fetch after a successful regen-stale POST so the
          banner reflects the in-flight queue. */}
      <FreshnessBanner
        refreshNonce={refreshNonce}
        onRegenStaleQueued={() => setRefreshNonce((n) => n + 1)}
      />

      {/* VISN-150 axis 1 — tenant-wide "Reprocess all stale"
          button. Distinct from the freshness banner's axis-2
          "Reingest all stale": that one re-runs the GENERATOR
          against existing aggregated_data.json (cheap, seconds);
          this one re-runs the PARSER from the raw PDF/XLSX
          (expensive, minutes). The button is always visible (the
          backend's diff is the source of truth for which rows
          actually get fanned out), so an operator who just
          deployed a new parser feature can fire it from any view.
          Confirm dialog lives in `reparseAllStale` itself. */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={reparseAllStale}
          disabled={reparseAllInFlight}
          data-testid="kg-reparse-stale-button"
          title={t(
            'common:knowledge_graph.reparse_stale_tooltip',
            'Re-run the raw-file parser on every upload whose parsed_features is behind the live catalog. Heavy — confirms first.',
          )}
          className={cn(
            'inline-flex items-center gap-2 rounded-md border border-yellow-400 px-3 py-1.5 text-xs font-medium transition-colors dark:border-yellow-700',
            reparseAllInFlight
              ? 'cursor-not-allowed text-gray-400 dark:text-gray-600'
              : 'text-yellow-900 hover:bg-yellow-50 dark:text-yellow-200 dark:hover:bg-yellow-900/20',
          )}
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5', reparseAllInFlight && 'animate-spin')}
          />
          {reparseAllInFlight
            ? t('common:knowledge_graph.reparse_stale_queueing', 'Queueing…')
            : t('common:knowledge_graph.reparse_stale_button', 'Reprocess all stale')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Pinned "needs attention" toggle. Defaults on so engineers see
            failed + stuck rows first — the default green-completed wall
            buried the 964-pnid backlog the audit found. */}
        <button
          type="button"
          onClick={() => {
            setNeedsAttentionOnly((v) => !v)
            setPage(1)
          }}
          data-testid="kg-needs-attention-toggle"
          data-active={needsAttentionOnly ? 'true' : 'false'}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
            needsAttentionOnly
              ? 'border-red-300 bg-red-50 text-red-800 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/30'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-dark-card dark:text-dark-text dark:hover:bg-dark-muted',
          )}
        >
          <AlertCircle className="h-4 w-4" />
          {t('common:knowledge_graph.needs_attention', 'Needs attention')}
        </button>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ProcessingStatus | '')
            setPage(1)
          }}
          data-testid="kg-status-filter"
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-dark-card dark:text-dark-text"
        >
          <option value="">{t('common:knowledge_graph.all_statuses', 'All statuses')}</option>
          {PROCESSING_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value as FileType | '')
            setPage(1)
          }}
          data-testid="kg-type-filter"
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-dark-card dark:text-dark-text"
        >
          <option value="">{t('common:knowledge_graph.all_types', 'All types')}</option>
          {FILE_TYPES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <div className="relative flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder={t(
              'common:knowledge_graph.search_placeholder',
              'Search filename…',
            )}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            data-testid="kg-search-input"
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-dark-card dark:text-dark-text"
          />
        </div>
      </div>

      {/* Table */}
      <div
        className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-card"
        data-testid="kg-uploads-table"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-muted">
              <tr>
                {([
                  'filename',
                  'type',
                  'size',
                  'status',
                  'retries',
                  'age',
                  'uploaded_at',
                  'error',
                  'actions',
                ] as const).map((col) => (
                  <th
                    key={col}
                    className="px-6 py-3 text-start text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400"
                  >
                    {t(`common:knowledge_graph.columns.${col}`, col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-dark-border dark:bg-dark-card">
              {loading ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-16 text-center text-sm text-gray-500 dark:text-dark-text-muted"
                  >
                    <div className="flex items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600" />
                    </div>
                  </td>
                </tr>
              ) : uploads.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-16 text-center text-sm text-gray-500 dark:text-dark-text-muted"
                    data-testid="kg-empty-state"
                  >
                    {t(
                      'common:knowledge_graph.no_uploads',
                      'No uploads match your filters.',
                    )}
                  </td>
                </tr>
              ) : (
                uploads.map((u) => {
                  // Fallback to the `pending` config if the backend
                  // returns a status the FE doesn't know about yet
                  // (e.g. a future `retrying` value). Without this
                  // guard the table crashes on every row when status
                  // unions drift. (greptile P1 #11684.)
                  const cfg = STATUS_CONFIG[u.processing_status] ?? STATUS_CONFIG.pending
                  const stuck = isStuck(u)
                  // Reingest is meaningful only when the upload already
                  // has an `aggregated_data.json` (i.e. ingestion ran).
                  // `pending` rows haven't been processed yet, so the
                  // regen worker would silently no-op (greptile P1
                  // #11869 follow-up). `queued`/`processing` rows have
                  // a live regen in flight; a second click would be a
                  // redundant queue entry.
                  const isReingestDisabled =
                    reingesting.has(u.id) ||
                    u.processing_status === 'pending' ||
                    u.processing_status === 'queued' ||
                    u.processing_status === 'processing'
                  return (
                    <tr
                      key={u.id}
                      className="transition-colors hover:bg-gray-50 dark:hover:bg-dark-muted"
                      data-testid={`kg-upload-row-${u.id}`}
                      data-stuck={stuck ? 'true' : 'false'}
                    >
                      <td className="px-6 py-4 max-w-[24rem]">
                        <div className="flex items-center gap-2">
                          {/* Red dot on rows where the server would
                              classify this as "stuck" (queued/processing
                              for >24h). Visible even outside
                              needs-attention view so the user catches
                              stuck rows when browsing All. */}
                          {stuck && (
                            <span
                              className="inline-block h-2 w-2 flex-shrink-0 rounded-full bg-red-500"
                              data-testid={`kg-stuck-dot-${u.id}`}
                              title={t(
                                'common:knowledge_graph.stuck_tooltip',
                                'Stuck >24h — processing has been in-flight longer than the threshold.',
                              )}
                            />
                          )}
                          <span
                            className="truncate text-sm font-medium text-gray-900 dark:text-dark-text"
                            title={u.original_filename}
                          >
                            {u.original_filename}
                          </span>
                          {/* VISN-150 axis 1 — "Reprocess available"
                              badge. Fires when the BE diff says this
                              row's `parsed_features` is missing tags
                              from EXPECTED_PARSED_FEATURES[file_type].
                              Tooltip lists the missing tags so the
                              operator knows what the row gains by
                              reprocessing. The corresponding
                              remediation is the per-row "Reprocess"
                              button rendered in the action column
                              (POSTs `/uploads/:id/reparse`); the
                              badge + button render under the same
                              two-gate check so they appear together.
                              Two-gate check (greptile P2 on PR #12114):
                              - `reprocess_recommended === true` so an
                                undefined-from-stale-fixture row
                                renders no badge.
                              - `processing_status === 'completed'` so
                                in-flight rows (pending / queued /
                                processing / failed) don't show
                                "Reprocess available" when the parser
                                hasn't legitimately finished. */}
                          {u.reprocess_recommended === true &&
                            u.processing_status === 'completed' && (
                            <span
                              className="inline-flex flex-shrink-0 items-center rounded-md border border-yellow-400 bg-yellow-50 px-1.5 py-0.5 text-[10px] font-medium text-yellow-900 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300"
                              data-testid={`kg-reprocess-badge-${u.id}`}
                              title={t(
                                'common:knowledge_graph.reprocess_tooltip',
                                {
                                  // i18next `{{features}}` placeholder must
                                  // appear in any translation override
                                  // for this key (greptile P2 on
                                  // PR #12114). The defaultValue below
                                  // uses the same `{{features}}` syntax
                                  // (not a TS template-literal embed) so
                                  // a translator who later adds the key
                                  // to the bundle WITHOUT `{{features}}`
                                  // will produce a tooltip with a
                                  // literal `{{features}}` string —
                                  // visible drift in localization QA
                                  // rather than a silent feature-list
                                  // drop.
                                  features: (u.missing_features ?? []).join(', ') || '(none)',
                                  defaultValue:
                                    "Parser ships features this upload doesn't have yet — missing: {{features}}. Reprocess from the upload's source to pick them up.",
                                },
                              )}
                            >
                              {t('common:knowledge_graph.reprocess_available', 'Reprocess available')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                          {u.file_type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {formatBytes(u.file_size)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
                            cfg.textClass,
                            cfg.bgClass,
                          )}
                          data-testid={`kg-status-${u.processing_status}`}
                        >
                          <span
                            className={cn('h-1.5 w-1.5 rounded-full', cfg.dotClass)}
                          />
                          {u.processing_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'text-xs',
                            u.processing_retry_count > 0
                              ? 'text-amber-700 dark:text-amber-400 font-medium'
                              : 'text-gray-500 dark:text-gray-400',
                          )}
                        >
                          {u.processing_retry_count}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="text-xs text-gray-600 dark:text-gray-400"
                          title={u.created_at}
                        >
                          {formatAge(u.created_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="whitespace-nowrap text-xs text-gray-600 dark:text-gray-400"
                          title={u.created_at}
                        >
                          {new Date(u.created_at).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 max-w-[20rem]">
                        {u.processing_error_message ? (
                          <span
                            className="truncate text-xs text-red-700 dark:text-red-400 block"
                            title={u.processing_error_message}
                          >
                            {u.processing_error_message}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {/* VISN-150 axis 1 — per-row "Reprocess"
                            button. Same gates as the "Reprocess
                            available" badge above so the badge and
                            button always appear together. POSTs
                            `/uploads/:id/reparse` which re-runs the
                            raw-file parser; the parser's success
                            path triggers kg-regen downstream so the
                            single click handles both halves.
                            Rendered BEFORE the Reingest button —
                            in the cases where a row needs both
                            actions, reparse is the right first move
                            (reingest against stale
                            `aggregated_data.json` would just bake
                            in the stale parsed_features). */}
                        <div className="inline-flex items-center gap-2">
                          {u.reprocess_recommended === true &&
                            u.processing_status === 'completed' && (
                              <button
                                type="button"
                                onClick={() => reparse(u.id)}
                                disabled={reparsing.has(u.id)}
                                data-testid={`kg-reparse-${u.id}`}
                                title={t(
                                  'common:knowledge_graph.reparse_tooltip',
                                  'Re-run the raw-file parser on this upload. Refreshes parsed_features and re-emits the KG. Heavier than Reingest — typically minutes for a PNID.',
                                )}
                                className={cn(
                                  'inline-flex items-center gap-1 rounded-md border border-yellow-400 px-2 py-1 text-xs font-medium transition-colors dark:border-yellow-700',
                                  reparsing.has(u.id)
                                    ? 'cursor-not-allowed text-gray-400 dark:text-gray-600'
                                    : 'text-yellow-900 hover:bg-yellow-50 dark:text-yellow-200 dark:hover:bg-yellow-900/20',
                                )}
                              >
                                <RefreshCw
                                  className={cn(
                                    'h-3 w-3',
                                    reparsing.has(u.id) && 'animate-spin',
                                  )}
                                />
                                {t('common:knowledge_graph.reparse', 'Reprocess')}
                              </button>
                            )}
                          {/* Reingest re-fires the regen pipeline
                              against the postgres-stored
                              aggregated_data.json — no raw-file
                              re-upload. See `isReingestDisabled`
                              above for the (single source of truth)
                              gating. */}
                          <button
                            type="button"
                            onClick={() => reingest(u.id)}
                            disabled={isReingestDisabled}
                            data-testid={`kg-reingest-${u.id}`}
                            title={t(
                              'common:knowledge_graph.reingest_tooltip',
                              'Re-run the KG generator against this upload\'s already-ingested data. Does NOT re-upload the file.',
                            )}
                            className={cn(
                              'inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium transition-colors dark:border-gray-600',
                              isReingestDisabled
                                ? 'cursor-not-allowed text-gray-400 dark:text-gray-600'
                                : 'text-gray-700 hover:bg-gray-50 dark:text-dark-text dark:hover:bg-dark-muted',
                            )}
                          >
                            <RefreshCw
                              className={cn(
                                'h-3 w-3',
                                reingesting.has(u.id) && 'animate-spin',
                              )}
                            />
                            {t('common:knowledge_graph.reingest', 'Reingest')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && total > 0 && (
          <Paginator
            currentPage={page}
            totalPages={Math.ceil(total / pageSize)}
            totalItems={total}
            pageSize={pageSize}
            setPage={setPage}
            // Reset to page 1 when the user changes page size. Without
            // this, picking "50 per page" while on page 10 fires a
            // request for page 10 of a recomputed pagination — usually
            // empty. Mirrors the setPage(1) call we use on every
            // other state change. (greptile P1 #11684.)
            setPageSize={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        )}
      </div>
    </div>
  )
}
